<script setup lang="ts">
// A conversation's settings, behind the gear in its header (HRTH-150). Everyone:
// notifications. A channel: rename and delete, for whoever manages channels. A DM: the
// group's name and leaving (not for DMs hosted on another server). Its people are the
// members panel's (MemberPanel, DmMembersPanel).
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { ConversationInfo } from '../api/types'
import { describeError } from '../api/http'
import { dmTitle } from '../lib/format'
import { can } from '../lib/roles'
import { useCall } from '../stores/call'
import { useSession } from '../stores/session'
import Modal from './Modal.vue'

const props = defineProps<{ conv: ConversationInfo }>()
const emit = defineEmits<{ close: [] }>()
const session = useSession()
const router = useRouter()
const call = useCall()

const space = computed(() => (props.conv.kind === 'channel' ? (session.spaces.find((s) => s.id === props.conv.space_id) ?? null) : null))
const canManage = computed(() => !!space.value && can(space.value, 'manage_channels'))
const group = computed(() => props.conv.kind === 'dm' && !!props.conv.group && !props.conv.host)
const title = computed(() =>
  props.conv.kind === 'channel' ? `#${props.conv.name ?? 'channel'}` : dmTitle(session.profiles, props.conv.members, session.me, props.conv.name),
)
const memberTitle = computed(() => dmTitle(session.profiles, props.conv.members, session.me))
const muted = computed(() => session.muted.has(props.conv.id))

const name = ref(props.conv.name ?? '')
const busy = ref(false)

async function run(fn: () => Promise<unknown>, ok?: string): Promise<void> {
  busy.value = true
  try {
    await fn()
    if (ok) session.toast(ok)
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}

const toggleMute = () => run(() => session.setMuted(props.conv.id, !muted.value))

/** The channel's colour tints its background; picked from the server palette. */
const color = computed(() => space.value?.channels.find((c) => c.id === props.conv.id)?.color ?? null)
const setColor = (c: string | null) => run(() => session.setChannelColor(space.value!.id, props.conv.id, c))

function renameChannel(): Promise<void> {
  const to = name.value.trim().toLowerCase().replace(/\s+/g, '-')
  return run(async () => {
    await session.renameSpace(space.value!.id, to, props.conv.id)
    name.value = to
  }, 'Channel renamed')
}

function deleteChannel(): Promise<void> {
  const sp = space.value!
  if (!confirm(`Delete ${title.value} and its history for everyone?`)) return Promise.resolve()
  return run(async () => {
    await session.deleteChannel(sp.id, props.conv.id)
    await router.replace({ name: 'space', params: { spaceId: sp.id } })
  }, 'Channel deleted')
}

const renameDm = (to: string | null) =>
  run(async () => {
    await session.renameDm(props.conv.id, to?.trim() || null)
    name.value = to?.trim() ?? ''
  })

function leaveDm(): void {
  if (!confirm(`Leave “${title.value}”? Its history is removed from this device.`)) return
  void run(async () => {
    if (call.conv === props.conv.id) await call.leave()
    await session.leaveDm(props.conv.id)
    await router.replace({ name: 'dm' })
  })
}
</script>

<template>
  <Modal page :title="conv.kind === 'channel' ? 'Channel settings' : 'Conversation settings'" @close="emit('close')">
    <div class="space-y-5" data-testid="conversation-settings">
      <form v-if="conv.kind === 'channel' && canManage" class="space-y-2" @submit.prevent="renameChannel">
        <label class="label" for="channel-name">Channel name</label>
        <div class="flex gap-2">
          <input id="channel-name" v-model="name" class="input" maxlength="48" data-testid="channel-name-input" />
          <button class="btn-primary" :disabled="busy || !name.trim() || name.trim() === conv.name" data-testid="rename-channel">Save</button>
        </div>
      </form>

      <form v-if="group" class="space-y-2" @submit.prevent="renameDm(name)">
        <label class="label" for="dm-name">Group name</label>
        <div class="flex gap-2">
          <input id="dm-name" v-model="name" class="input" :placeholder="memberTitle" maxlength="64" data-testid="dm-name-input" />
          <button class="btn-primary" :disabled="busy || name.trim() === (conv.name ?? '')" data-testid="dm-name-save">Save</button>
        </div>
        <p class="mono text-text-muted">
          Only the people in it can see the name.
          <button v-if="conv.name" type="button" class="underline hover:text-text-primary" data-testid="dm-name-clear" @click="renameDm(null)">
            Use their names instead
          </button>
        </p>
      </form>

      <div v-if="conv.kind === 'channel' && canManage" data-testid="channel-color">
        <div class="label">Colour</div>
        <div class="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Channel colour">
          <button
            class="mono flex h-8 items-center border px-2 text-text-muted uppercase"
            :class="color === null ? 'border-ink text-text-primary' : 'border-border hover:border-border-strong'"
            role="radio"
            :aria-checked="color === null"
            :disabled="busy"
            data-testid="channel-color-none"
            @click="setColor(null)"
          >
            None
          </button>
          <button
            v-for="c in session.palette"
            :key="c"
            class="h-8 w-8 border p-1"
            :class="color?.toLowerCase() === c.toLowerCase() ? 'border-ink' : 'border-border hover:border-border-strong'"
            role="radio"
            :aria-checked="color?.toLowerCase() === c.toLowerCase()"
            :aria-label="c"
            :disabled="busy"
            :data-testid="`channel-color-${c}`"
            @click="setColor(c)"
          >
            <span class="block h-full w-full" :style="{ background: c }" />
          </button>
        </div>
        <p class="mono mt-1.5 text-text-muted">Tints the channel's background. Only the space's members can see it.</p>
      </div>

      <div>
        <div class="label">Notifications</div>
        <label class="flex cursor-pointer items-center gap-3 text-sm">
          <input type="checkbox" :checked="!muted" :disabled="busy" data-testid="settings-notify" @change="toggleMute" />
          <span>
            Notify me about new messages in {{ title }}
            <span class="block text-xs text-text-muted">On all your devices.</span>
          </span>
        </label>
      </div>

      <div v-if="conv.kind === 'channel' && canManage" class="border-t border-border pt-4">
        <button
          class="btn-danger w-full"
          :disabled="busy || (space?.channels.length ?? 0) <= 1"
          :title="(space?.channels.length ?? 0) <= 1 ? 'A space keeps at least one channel' : undefined"
          data-testid="delete-channel"
          @click="deleteChannel"
        >
          Delete channel
        </button>
      </div>
      <div v-if="conv.kind === 'dm' && !conv.host" class="border-t border-border pt-4">
        <button class="btn-ghost text-error hover:border-signal hover:text-error" :disabled="busy" data-testid="leave-dm" @click="leaveDm">
          Leave
        </button>
      </div>
    </div>
  </Modal>
</template>
